import { SystemWorkOrderStatus } from '../job/system.workorder.status';
import { WorkOrder } from '../job/workorder';
import { PendingCode } from '../shared/code';
import { summariseGateItems } from '../tasks/gate.item.summary';
import { findUnresolvedGatedTasks, StageCompletionBlocker } from '../tasks/stage.completion.gate';
import { Task } from '../tasks/task';
import { TaskGate } from '../tasks/task.evidence';
import { isPendingTask } from '../tasks/task.status.policy';
import { isItemRemoved } from '../../utilities/item.status.utility';

/** One pending line holding the claim; the status name is the tenant's own, never fixed wording. */
export interface PendingLineBlocker {
    lineId: string;
    name: string;
    statusName: string;
}

/** Everything holding a stage's claim, so one refusal names every reason at once. */
export interface ClaimSubmissionRefusal {
    /** The stage's own status name while it is not yet Completed; absent once it is. */
    stageStatusName?: string;
    pendingLines: PendingLineBlocker[];
    tasks: StageCompletionBlocker[];
}

export const CLAIM_SUBMISSION_BLOCKED_CODE = 'ClaimSubmissionBlocked';

/** Both gates hold a claim: a Stage Completion task can still be open here if the stage was completed by override or the task was reopened. */
const CLAIM_HOLDING_GATES: readonly TaskGate[] = [TaskGate.StageCompletion, TaskGate.ClaimSubmission];

/**
 * The claim submission gate: status only, never evidence (evidence was checked when each task was completed).
 * Callers pass only the tasks the claiming party can see. Returns null when nothing holds the claim.
 * `pendingHoldsClaim` is the tenant's choice (WBS-13379): off, a pending line holds nothing, and neither does its Pending task, the same fact seen from the task.
 */
export function findClaimSubmissionRefusal(stage: WorkOrder, visibleTasks: readonly Task[] | null | undefined, pendingHoldsClaim = true): ClaimSubmissionRefusal | null {

    const refusal: ClaimSubmissionRefusal = {
        stageStatusName: stage?.status?.systemStatus === SystemWorkOrderStatus.Completed ? undefined : stage?.status?.name ?? stage?.status?.systemStatus ?? '',
        pendingLines: pendingHoldsClaim ? findPendingLines(stage?.claimCodes) : [],
        tasks: findUnresolvedGatedTasks(pendingHoldsClaim ? visibleTasks : withoutPendingTasks(visibleTasks), CLAIM_HOLDING_GATES),
    };

    return refusal.stageStatusName === undefined && !refusal.pendingLines.length && !refusal.tasks.length ? null : refusal;
}

/**
 * Every pending line holds the claim, SOR or not (Fayez, 6 October 2026): approving the claim would otherwise approve lines nobody reviewed.
 * Pending is decided by the array, never the stored isClaimCode flag, which older lines lack; a withdrawn or declined line no longer waits.
 */
function findPendingLines(claimCodes: readonly PendingCode[] | null | undefined): PendingLineBlocker[] {

    return (claimCodes ?? [])
        .filter(line => !!line?._id && line.active !== false && !isItemRemoved(line, true))
        .map(line => ({ lineId: line._id, name: line.name ?? line.description ?? '', statusName: line.itemStatus?.name ?? '' }));
}

/** Only a pending line's task is ever Pending, and only approving or declining the line moves it out, so it holds the claim exactly when its line would. */
function withoutPendingTasks(tasks: readonly Task[] | null | undefined): Task[] {

    return (tasks ?? []).filter(task => !isPendingTask(task));
}

/** Open tasks are the only reason a permitted office user may approve past; a pending line or an uncompleted stage never is. */
export function isClaimRefusalOverridable(refusal: ClaimSubmissionRefusal | null | undefined): boolean {

    return !!refusal && refusal.stageStatusName === undefined && !refusal.pendingLines.length && refusal.tasks.length > 0;
}

/** One sentence per kind of reason, in the order the subcontractor would clear them, each naming up to the first five items. */
export function describeClaimSubmissionRefusal(refusal: ClaimSubmissionRefusal): string[] {

    const lines = refusal.pendingLines.map(line => `${line.name} (${line.statusName})`);
    const tasks = refusal.tasks.map(task => `${task.name} (${task.statusName})`);

    return [
        ...(refusal.stageStatusName === undefined ? [] : [`The stage must be completed before the claim (it is ${refusal.stageStatusName}).`]),
        ...(lines.length ? [`${summariseGateItems(lines, ['item', 'items'])} must be approved or declined before the claim.`] : []),
        ...(tasks.length ? [`${summariseGateItems(tasks, ['task', 'tasks'])} must be resolved before the claim.`] : []),
    ];
}
